import { Link } from 'react-router';
import { motion } from 'framer-motion';
import { Button, Card, CardContent, CardHeader, CardTitle } from '@dzseguidores/ui';
import type { ProductPublic } from '@dzseguidores/types';
import { NetworkIcon } from '@/components/network-icon';
import { PriceBlock } from '@/components/cro/price-block';
import { badgeForProduct, badgeLabel, serviceLabel } from '@/lib/cro';
import { useCartStore } from '@/stores/cart-store';

type Props = {
  product: ProductPublic;
  peers?: ProductPublic[];
  index?: number;
  cta?: 'add' | 'buy';
};

export function ProductCard({ product, peers = [], index = 0, cta = 'add' }: Props) {
  const addItem = useCartStore((s) => s.addItem);
  const badge = badgeForProduct(product, peers.length ? peers : [product]);
  const label = badgeLabel(badge);
  const qtyLabel = product.minQty.toLocaleString('pt-BR');
  const service = serviceLabel(product.serviceTypeSlug);

  function addToCart() {
    addItem({
      productId: product.id,
      slug: product.slug,
      name: product.name,
      priceCents: product.promoPriceCents ?? product.priceCents,
      quantity: 1,
    });
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: Math.min(index * 0.04, 0.24) }}
      className="h-full"
    >
      <Card
        className={`glass relative flex h-full flex-col ${
          badge === 'popular' ? 'ring-1 ring-[var(--color-accent)]' : ''
        }`}
      >
        {label && (
          <span
            className={`absolute -top-2.5 left-4 rounded-md px-2 py-0.5 text-[11px] font-semibold tracking-wide ${
              badge === 'popular'
                ? 'bg-[var(--color-accent)] text-[var(--color-accent-fg)]'
                : 'bg-surface text-fg border border-border'
            }`}
          >
            {label}
          </span>
        )}
        <CardHeader className={label ? 'pt-6' : undefined}>
          <div className="mb-2 inline-flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-muted">
            <NetworkIcon
              slug={product.networkSlug}
              name={product.networkName ?? product.networkSlug}
              size={16}
            />
            <span>{product.networkName ?? product.networkSlug}</span>
            <span aria-hidden>·</span>
            <span>{service}</span>
          </div>
          <CardTitle className="text-lg text-fg">
            <Link to={`/produto/${product.slug}`} className="hover:underline">
              {product.name}
            </Link>
          </CardTitle>
        </CardHeader>
        <CardContent className="mt-auto flex flex-1 flex-col space-y-4">
          <p className="line-clamp-2 text-sm text-muted">{product.description}</p>
          <p className="text-xs text-muted">
            Entrega de <span className="font-medium text-fg">{qtyLabel}</span> {service}
          </p>
          <div className="mt-auto flex items-end justify-between gap-3">
            <PriceBlock
              priceCents={product.priceCents}
              promoPriceCents={product.promoPriceCents}
              size="md"
            />
            <div className="flex shrink-0 gap-2">
              <Link to={`/produto/${product.slug}`}>
                <Button variant="ghost" size="sm">
                  Detalhes
                </Button>
              </Link>
              {cta === 'buy' ? (
                <Link to={`/produto/${product.slug}`}>
                  <Button size="sm">Comprar</Button>
                </Link>
              ) : (
                <Button size="sm" onClick={addToCart}>
                  Adicionar
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
}
